import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { LogOut, Home, Clock, Calendar, Trash2, Save } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;
const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function AdminScheduling() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('availability');
  const [slots, setSlots] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBlackout, setNewBlackout] = useState({ date: '', reason: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [availRes, bookRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/availability`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/scheduled-bookings`, { withCredentials: true }),
      ]);
      setSlots(availRes.data.slots);
      setBlackouts(availRes.data.blackouts);
      setBookings(bookRes.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const toggleDay = (dayIndex) => {
    const existing = slots.find(s => s.day_of_week === dayIndex);
    if (existing) {
      setSlots(prev => prev.map(s => s.day_of_week === dayIndex ? { ...s, is_active: !s.is_active } : s));
    } else {
      setSlots(prev => [...prev, { day_of_week: dayIndex, start_time: '09:00', end_time: '17:00', is_active: true }]);
    }
  };

  const updateSlotTime = (dayIndex, field, value) => {
    setSlots(prev => prev.map(s => s.day_of_week === dayIndex ? { ...s, [field]: value } : s));
  };

  const saveAvailability = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/availability`, { slots }, { withCredentials: true });
      toast.success('Availability saved');
    } catch { toast.error('Failed to save'); }
  };

  const addBlackout = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/blackout-dates`, newBlackout, { withCredentials: true });
      toast.success('Blackout date added');
      setNewBlackout({ date: '', reason: '' });
      fetchAll();
    } catch { toast.error('Failed to add'); }
  };

  const removeBlackout = async (date) => {
    try {
      await axios.delete(`${API_URL}/api/admin/blackout-dates/${date}`, { withCredentials: true });
      toast.success('Removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/scheduled-bookings/${bookingId}`, { status }, { withCredentials: true });
      toast.success('Updated');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };
  if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#1D4ED8] border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <AdminLayout title="Scheduling"><div>
        <div className="flex gap-3 mb-6">
          {[{k:'availability',l:'Availability',ic:Clock},{k:'blackouts',l:'Blackout Dates',ic:Calendar},{k:'bookings',l:'Scheduled Bookings',ic:Calendar}].map(t => (
            <button key={t.k} onClick={() => setActiveTab(t.k)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${activeTab === t.k ? 'bg-[#1D4ED8] text-white border-[#1D4ED8]' : 'bg-white text-[#475569] border-[#E2E8F0]'}`} data-testid={`tab-${t.k}`}><t.ic size={16} />{t.l}</button>
          ))}
        </div>

        {activeTab === 'availability' && (
          <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
            <h2 className="text-lg font-bold text-[#0A0F1C] mb-4" data-testid="avail-title">Weekly Availability</h2>
            <div className="space-y-3">
              {dayNames.map((name, i) => {
                const slot = slots.find(s => s.day_of_week === i);
                const active = slot?.is_active;
                return (
                  <div key={i} className={`flex items-center gap-4 p-3 border rounded-lg ${active ? 'border-[#1D4ED8]/20 bg-[#EFF6FF]' : 'border-[#E2E8F0]'}`} data-testid={`day-${i}`}>
                    <label className="flex items-center gap-2 w-32">
                      <input type="checkbox" checked={!!active} onChange={() => toggleDay(i)} className="rounded" />
                      <span className={`text-sm font-medium ${active ? 'text-[#0A0F1C]' : 'text-[#94A3B8]'}`}>{name}</span>
                    </label>
                    {active && (
                      <div className="flex items-center gap-2">
                        <Input type="time" value={slot?.start_time || '09:00'} onChange={(e) => updateSlotTime(i, 'start_time', e.target.value)} className="w-32" />
                        <span className="text-[#94A3B8]">to</span>
                        <Input type="time" value={slot?.end_time || '17:00'} onChange={(e) => updateSlotTime(i, 'end_time', e.target.value)} className="w-32" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <Button onClick={saveAvailability} className="btn-primary-blue mt-4 flex items-center gap-2" data-testid="save-avail-btn"><Save size={16} /> Save Availability</Button>
          </div>
        )}

        {activeTab === 'blackouts' && (
          <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
            <h2 className="text-lg font-bold text-[#0A0F1C] mb-4">Blackout Dates</h2>
            <form onSubmit={addBlackout} className="flex gap-3 mb-6">
              <Input type="date" value={newBlackout.date} onChange={(e) => setNewBlackout({...newBlackout, date: e.target.value})} required data-testid="blackout-date" />
              <Input placeholder="Reason (optional)" value={newBlackout.reason} onChange={(e) => setNewBlackout({...newBlackout, reason: e.target.value})} className="flex-grow" />
              <Button type="submit" className="btn-primary-blue">Add</Button>
            </form>
            {blackouts.length > 0 ? (
              <div className="space-y-2">
                {blackouts.map((b, i) => (
                  <div key={b.date} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-lg" data-testid={`blackout-${i}`}>
                    <div><span className="font-medium text-[#0A0F1C]">{b.date}</span>{b.reason && <span className="text-sm text-[#475569] ml-2">{b.reason}</span>}</div>
                    <button onClick={() => removeBlackout(b.date)} className="text-red-600 hover:bg-red-50 p-2 rounded"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            ) : <p className="text-[#475569] text-center py-4">No blackout dates set</p>}
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="bg-white p-6 border border-[#E2E8F0] rounded-lg">
            <h2 className="text-lg font-bold text-[#0A0F1C] mb-4" data-testid="bookings-title">Scheduled Bookings ({bookings.length})</h2>
            {bookings.length > 0 ? (
              <div className="space-y-3">
                {bookings.map((b, i) => (
                  <div key={b.booking_id} className="p-4 border border-[#E2E8F0] rounded-lg" data-testid={`sched-booking-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-bold text-[#0A0F1C]">{b.name}</span>
                        <span className="text-sm text-[#475569] ml-2">{b.email}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${b.status === 'confirmed' ? 'bg-green-100 text-green-700' : b.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-[#F1F5F9] text-[#475569]'}`}>{b.status}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-[#475569]">
                      <span>{b.date}</span><span>{b.time}</span><span className="capitalize">{b.session_type?.replace(/_/g, ' ')}</span>
                    </div>
                    {b.notes && <p className="text-sm text-[#94A3B8] mt-1">{b.notes}</p>}
                    {b.status === 'confirmed' && (
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => updateBookingStatus(b.booking_id, 'completed')} className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200">Complete</button>
                        <button onClick={() => updateBookingStatus(b.booking_id, 'cancelled')} className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200">Cancel</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="text-[#475569] text-center py-8">No scheduled bookings</p>}
          </div>
        )}
      </div></AdminLayout>);}
